/* eslint-disable react/no-array-index-key */
import React, { JSX, useEffect, useState } from "react";

import { DashboardReportsApi } from "api/DashboardReportsApi";
import { DweenCalendarEnum, DweenTimeUnitEnum } from "utils/enums";
import { useGetDashboard, useUpdateDashboard } from "api/api-hooks/dashboards-hooks";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { T } from "services/lang/LanguageService";
import {
  DashboardComponentAlign,
  DashboardComponentGroup,
  DashboardComponentJustify,
} from "api/model/dashboard/DashboardComponentGroup";
import ImportDashboardOrTab from "components/fragments/dashboard/components/ImportDashboardOrTab";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { DashboardLayoutType } from "api/model/dashboard/DashboardComponent";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import { Instant } from "@js-joda/core";
import { useCompanyTimezone } from "hooks/useTimezone";
import { Toolbar } from "primereact/toolbar";
import { useUserState } from "store/user-store";
import { addToDate, truncateDate } from "utils/time-utils";
import { APIError } from "services/HttpService";
import { dashboardUpdateErrorHandler, isDweenCalendarEnum } from "components/fragments/dashboard/utils";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import GeneratedReportTable from "../../dashboard/generated-report-table/GeneratedReportTable";
import { DashboardComponentMappingWrapper } from "../dashboard/components/dashboard-utils";
import DashboardReportEditor from "./DashboardReportEditor";

export default function DashboardReportPage() {
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [printModeToggled, setPrintModeToggled] = useState(false);
  const [isImportDashboardOrTabDialogVisible, setIsImportDashboardOrTabDialogVisible] = useState(false);
  const [refTime, setRefTime] = useState<Date | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState(0);
  const [updatedDashboard, setUpdatedDashboard] = useState<Dashboard | undefined>(undefined);
  const language = useUserState(state => state.getLanguage());

  // dashboardId from query params
  const dashboardId = new URLSearchParams(window.location.search).get("dashboardId")!!;
  const id = parseInt(dashboardId, 10);

  const { data, isSuccess } = useGetDashboard(id);
  const companyTz = useCompanyTimezone();

  useEffect(() => {
    if (data && isSuccess) {
      let truncateTo = data?.reportOptions?.timingOptions?.referenceTimeTruncateTo || DweenTimeUnitEnum.HOURS;
      if (truncateTo === DweenCalendarEnum.CALENDAR_445) {
        truncateTo = DweenTimeUnitEnum.HOURS;
      }
      const offset = data?.reportOptions?.timingOptions?.referenceTimeOffset ?? 0;
      const offsetUnit =
        data?.reportOptions?.timingOptions?.referenceTimeOffsetUnit || DweenTimeUnitEnum.HOURS;
      let referenceTime = truncateDate(Instant.now(), truncateTo, companyTz);
      referenceTime = addToDate(referenceTime, offset, offsetUnit);
      setRefTime(referenceTime);
    }
  }, [data, isSuccess]);

  const updateQuery = useUpdateDashboard({
    onSuccess: () => {
      easasAlert({
        summary: T("alert.success"),
        detail: T("alert.dashboard-updated"),
        severity: "success",
        life: 3000,
      });
      setShowConfigDialog(false);
    },
    onFailure: (error: APIError) => {
      dashboardUpdateErrorHandler(error, updateQuery.mutate, updatedDashboard!);
    },
  });

  const openPreviewTab = () => {
    if (!refTime) {
      easasAlert({
        summary: T("alert.warning"),
        detail: T("alert.reference-time-not-initiated"),
        severity: "warn",
        life: 3000,
      });
      return;
    }

    window
      .open(
        DashboardReportsApi.getPreviewUrl(
          id,
          refTime,
          data?.reportOptions.language ?? language,
          data?.reportOptions?.meta?.jsBuild
        ),
        "_blank"
      )
      ?.focus();
  };

  const genAndDlPdf = () => {
    if (!refTime) {
      easasAlert({
        summary: T("alert.warning"),
        detail: T("alert.reference-time-not-initiated"),
        severity: "warn",
        life: 3000,
      });
      return;
    }

    window
      .open(
        DashboardReportsApi.getGenerationAndDownloadUrl({
          dashboardId: id,
          referenceTime: refTime.toISOString(),
        }),
        "_blank"
      )
      ?.focus();
  };

  const updateDashboard = (dashboard: Dashboard) => {
    setUpdatedDashboard(dashboard);
    updateQuery.mutate(dashboard);
  };

  const leftContents = (
    <>
      <Button
        label={T("action.edit")}
        icon="pi pi-pencil"
        className="dashboard-editor-button icon-blue"
        onClick={() => setShowConfigDialog(true)}
      />
      <Button
        label={T("action.toggle-print-mode")}
        icon="pi pi-print"
        className="dashboard-editor-button icon-orange"
        onClick={() => setPrintModeToggled(!printModeToggled)}
      />
      <Button
        label={T("action.import-dashboard-or-tab")}
        icon="pi pi-file-import"
        className="dashboard-editor-button icon-green"
        onClick={() => setIsImportDashboardOrTabDialogVisible(true)}
      />
    </>
  );

  const rightContents = (
    <>
      <CalendarWrapper
        placeholder={T("load-conf.reference-time")}
        dateFormat="yy-mm-dd"
        className="mr-2"
        value={refTime}
        onChange={setRefTime}
      />
      <Button
        label={T("action.generate-and-download-pdf")}
        icon="pi pi-file-pdf"
        className="dashboard-editor-button icon-orange"
        onClick={genAndDlPdf}
      />
      <Button
        label={T("word.preview")}
        icon="pi pi-file"
        className="dashboard-editor-button icon-orange"
        onClick={openPreviewTab}
      />
    </>
  );

  const renderBody = () => {
    return (
      <>
        <h1>
          {T("word.dashboard")} {data?.title}
        </h1>

        {data?.dashboardTabsJs?.map((c, tabIdx) => {
          return (
            <div key={tabIdx} style={{ border: "dotted 2px", padding: "0.5rem", marginTop: "2rem" }}>
              <h3>
                {T("word.tab")} {tabIdx}: {c.title}
              </h3>

              {c.componentGroups?.map((g, groupIdx) => {
                return (
                  <ComponentGroupWrapper key={groupIdx} i={groupIdx} group={g}>
                    {g.components?.map((component, componentIdx) => {
                      return (
                        <div
                          key={componentIdx}
                          style={{
                            border: "solid 1px",
                            marginTop: "2.5rem",
                            background: "white",
                          }}
                        >
                          <h5>
                            {T("word.component")} {componentIdx}: {component.title} [{component.type}]
                          </h5>

                          <DashboardComponentMappingWrapper
                            id={`${component.type}-${tabIdx}-${groupIdx}-${componentIdx}`}
                            component={component.type}
                            config={component.config}
                            referenceTime={refTime!!.toISOString()}
                            calendarType={
                              isDweenCalendarEnum(data?.reportOptions.timingOptions.referenceTimeTruncateTo)
                                ? data?.reportOptions.timingOptions.referenceTimeTruncateTo
                                : undefined
                            }
                          />
                        </div>
                      );
                    })}
                  </ComponentGroupWrapper>
                );
              })}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className={printModeToggled ? "print-mode-enabled" : ""}>
      <Toolbar left={leftContents} right={rightContents} />

      <TabView activeIndex={activeIndex} onTabChange={e => setActiveIndex(e.index)}>
        <TabPanel header={T("word.preview")}>
          {!data || (!refTime && T("message.loading-dashboard-config"))}
          {data && refTime && renderBody()}
        </TabPanel>
        <TabPanel header={T("word.controls")}>
          <GeneratedReportTable
            ids={[id]}
            superUserComponent
            loadingConfig={{
              truncateStartUnit: DweenTimeUnitEnum.DAYS,
              offsetUnit: DweenTimeUnitEnum.DAYS,
              offset: 1,
              periodUnit: DweenTimeUnitEnum.DAYS,
              period: -30,
            }}
          />
        </TabPanel>
      </TabView>

      <Dialog
        header={T("action.edit-dashboard")}
        visible={showConfigDialog}
        contentStyle={{ overflow: "initial" }}
        style={{ width: "min(100%, 1000px)", height: "70vh" }}
        onHide={() => {
          setShowConfigDialog(false);
        }}
      >
        <DashboardReportEditor
          dashboard={data!!}
          onChange={updateDashboard}
          cancel={() => setShowConfigDialog(false)}
        />
      </Dialog>
      {isImportDashboardOrTabDialogVisible && data && (
        <ImportDashboardOrTab
          dashboard={data}
          isImportDashboardDialogVisible={isImportDashboardOrTabDialogVisible}
          setIsImportDashboardDialogVisibility={setIsImportDashboardOrTabDialogVisible}
          isReportMode
        />
      )}
    </div>
  );
}

const ComponentGroupWrapper = (props: {
  group: DashboardComponentGroup;
  children: JSX.Element | JSX.Element[];
  i: number;
}) => {
  return (
    <div style={{ border: "dashed 2px", padding: "1rem", marginTop: "1rem", background: "lightpink" }}>
      <h4>
        {T("word.component-group")}
        {props.i + 1}
      </h4>

      <ComponentGroupLayoutWrapper group={props.group}>{props.children}</ComponentGroupLayoutWrapper>
    </div>
  );
};

export const ComponentGroupLayoutWrapper = (props: {
  group: DashboardComponentGroup;
  children: JSX.Element | JSX.Element[];
}) => {
  const { layout } = props.group;
  const type = layout.type ?? DashboardLayoutType.VERTICAL;
  const { reportOptions } = props.group;

  let className = "";

  if (type === DashboardLayoutType.HORIZONTAL_FLEX) {
    className = `${reportOptions?.fullWidth ? "full-bleed" : ""} ${reportOptions?.theme?.toLocaleLowerCase() ?? ""} flex justify-content-${(layout.justify ?? DashboardComponentJustify.BETWEEN).toLowerCase()} align-items-${(layout.align ?? DashboardComponentAlign.CENTER).toLowerCase()}  gap-${layout.gap ?? 2}`;
  }

  if (type === DashboardLayoutType.VERTICAL) {
    className = `${reportOptions?.fullWidth ? "full-bleed" : ""} ${reportOptions?.theme?.toLocaleLowerCase() ?? ""} flex flex-column`;
  }

  return <div className={className}>{props.children}</div>;
};
